// Fullbay invoice history → Asena Bay: Reports → Revenue → All Revenue Report (Download "Invoice list" + "Invoice details")
// and Payments Report (Download "Details"). Invoices keep their Fullbay numbers, lines, tax and totals; payments keep date,
// method and reference; what's still owed comes out the same as Fullbay's "Balance".

import type { FbSheet } from './fullbay';

export type HistKind = 'invoiceList' | 'invoiceDetails' | 'paymentDetails' | 'paymentSummary' | null;
const has = (h: string[], ...c: string[]) => c.every(x => h.includes(x));

export function historyKind(headers: string[]): HistKind {
  const h = headers.map(x => x.trim());
  if (has(h, 'Number', 'Invoice Date', 'Item', 'Total', 'Sales Total')) return 'invoiceDetails';
  if (has(h, 'Number', 'Invoice Date', 'Amount', 'Balance', 'Subtotal')) return 'invoiceList';
  if (has(h, 'Transaction #', 'Payment / Credit Used', 'Payment Date')) return 'paymentDetails';
  if (has(h, 'Received Total', 'Payment Method', 'Number', 'Date')) return 'paymentSummary';
  return null;
}

export const HIST_LABEL: Record<Exclude<HistKind, null>, string> = {
  invoiceList: 'Old invoices (list with balances)', invoiceDetails: 'Old invoice lines (labor, parts, fees)',
  paymentDetails: 'Payments (which invoice each paid)', paymentSummary: 'Payments summary (not needed)',
};

/** total = the line's exact amount in cents (Fullbay rounds per line); price / cost are per unit. */
export type HistLine = { kind: 'labor' | 'part' | 'misc'; job: string; description: string; part_no?: string; qty: number; price: number; total: number; cost?: number; hours?: number; rate?: number; taxable?: boolean };
export type HistPayment = { date: string; amount: number; method: string; reference: string };
export type HistInvoice = {
  number: string; order: string; date: string; customer: string; unit: string; po: string; writer: string; terms: string;
  mileage: number; lines: HistLine[]; supplies: number; tax: number; total: number; balance: number; payments: HistPayment[]; note: string;
};

const cents = (x: unknown) => { const n = Number(String(x ?? '').replace(/[$,\s]/g, '')); return Number.isFinite(n) ? Math.round(n * 100) : 0; };
const num = (x: unknown) => { const n = Number(String(x ?? '').replace(/[,\s]/g, '')); return Number.isFinite(n) ? n : 0; };
/** "3/6/2026" → "2026-03-06" */
export function usDate(x: string) {
  const m = String(x || '').match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
  return m ? `${m[3]}-${m[1].padStart(2, '0')}-${m[2].padStart(2, '0')}` : '';
}
const objs = (s: FbSheet) => s.rows.map(r => Object.fromEntries(s.headers.map((h, i) => [h.trim(), (r[i] ?? '').trim()])) as Record<string, string>);

/** Card payments come as "Visa - XXXXXXXXXX-5989": method Card, last four kept as the reference. */
function payMethod(raw: string, ref: string) {
  const m = raw.match(/^(Visa|Mastercard|Amex|Discover)\b.*?(\d{4})\s*$/i);
  if (m) return { method: 'Card', reference: `${m[1]} ••${m[2]}` };
  const known: Record<string, string> = { zelle: 'Zelle', ach: 'ACH', efs: 'EFS', cash: 'Cash', check: 'Check', credit: 'Credit' };
  return { method: known[raw.toLowerCase()] || raw || 'Other', reference: ref };
}

export function historyFromFullbay(sheets: FbSheet[]) {
  const by = (k: HistKind) => sheets.filter(s => historyKind(s.headers) === k).flatMap(objs);
  const list = by('invoiceList');
  const details = new Map<string, Record<string, string>[]>();
  for (const d of by('invoiceDetails')) details.set(d['Number'], [...(details.get(d['Number']) || []), d]);
  const pays = new Map<string, HistPayment[]>();
  for (const p of by('paymentDetails')) {
    const amt = cents(p['Payment / Credit Used']);
    if (amt <= 0) continue;
    const pm = payMethod(p['Payment Method'], p['Reference #']);
    pays.set(p['Transaction #'], [...(pays.get(p['Transaction #']) || []), { date: usDate(p['Payment Date']), amount: amt, ...pm }]);
  }
  const notes: string[] = [];
  let fixed = 0;
  const invoices: HistInvoice[] = [];
  // One Fullbay invoice can cover several service orders / units (one row each in the list): they become one invoice.
  const groups = new Map<string, Record<string, string>[]>();
  for (const r of list) groups.set(r['Number'], [...(groups.get(r['Number']) || []), r]);
  for (const rows of groups.values()) {
    const r = { ...rows[0] };
    for (const k of ['Subtotal', 'Tax', 'Amount', 'Balance', 'Labor', 'Parts', 'Sublet', 'Service Call', 'Mileage', 'Misc', 'Discount', 'Supplies'])
      r[k] = String(rows.reduce((a, x) => a + num(x[k]), 0));
    const units = [...new Set(rows.map(x => x['Unit']).filter(Boolean))];
    r['Unit'] = units.join(', ');
    r['Order'] = [...new Set(rows.map(x => x['Order']).filter(Boolean))].join(', ');
    const subtotal = cents(r['Subtotal']);
    const many = units.length > 1;
    const ds = (details.get(r['Number']) || []).map(d => (many && d['Unit'] ? { ...d, 'Complaint Description': `#${d['Unit']} — ${d['Complaint Description'] || d['Service Description'] || 'Work'}` } : d));
    const lines: HistLine[] = [];
    let supplies = 0;
    for (const d of ds) {
      const total = cents(d['Total']);
      const job = d['Complaint Description'] || d['Service Description'] || '';
      if (d['Type'] === 'Shop Supplies') { supplies += total; continue; }
      if (d['Type'] === 'Labor') {
        lines.push({ kind: 'labor', job, description: d['Service Description'] || d['Item Display Title'] || 'Labor', qty: 1, price: total, total, hours: Math.round(num(d['Qty']) * 100), rate: cents(d['Rate']), taxable: !/non-taxable/i.test(d['Item']) });
      } else if (d['Type'] === 'Part') {
        lines.push({ kind: 'part', job, description: d['Part Description'] || d['Item Display Title'] || 'Part', part_no: d['Part #'], qty: num(d['Qty']) || 1, price: cents(d['Rate']), total, cost: cents(d['Part Cost']), taxable: !/non-taxable/i.test(d['Item']) });
      } else if (total) {
        lines.push({ kind: 'misc', job, description: d['Item Display Title'] || d['Service Description'] || d['Item'] || 'Misc', qty: 1, price: total, total, taxable: !/non-taxable/i.test(d['Item']) });
      }
    }
    const lineSum = lines.reduce((a, l) => a + l.total, 0) + supplies;
    let use = lines;
    let note = '';
    // Fullbay rounds each line to the cent; a few cents of difference go on the biggest line so totals match exactly.
    if (ds.length && lines.length && lineSum !== subtotal && Math.abs(lineSum - subtotal) <= 100) {
      const big = lines.reduce((a, l) => (Math.abs(l.total) > Math.abs(a.total) ? l : a), lines[0]);
      big.total += subtotal - lineSum;
    }
    const lineSum2 = lines.reduce((a, l) => a + l.total, 0) + supplies;
    // A few Fullbay invoices were changed after a credit / refund: their line detail no longer adds up to the invoice.
    // Those come in as summary lines that match Fullbay's totals.
    if (!ds.length || lineSum2 !== subtotal) {
      if (ds.length) { fixed++; note = 'Lines summarised: Fullbay shows a credit / change on this invoice.'; }
      use = [];
      const add = (kind: HistLine['kind'], description: string, v: number) => v && use.push({ kind, job: '', description, qty: 1, price: v, total: v, cost: 0 });
      add('labor', 'Labor', cents(r['Labor']));
      add('part', 'Parts', cents(r['Parts']));
      add('misc', 'Sublet', cents(r['Sublet']));
      add('misc', 'Service call', cents(r['Service Call']));
      add('misc', 'Mileage', cents(r['Mileage']));
      add('misc', 'Misc', cents(r['Misc']));
      add('misc', 'Discount', cents(r['Discount']));
      supplies = cents(r['Supplies']);
    }
    const total = cents(r['Amount']), balance = Math.max(0, cents(r['Balance']));
    // Payments: what Fullbay recorded, never more than the invoice minus what's still open.
    let room = total - balance;
    const ps: HistPayment[] = [];
    for (const p of (pays.get(r['Number']) || []).sort((a, b) => a.date.localeCompare(b.date))) {
      const a = Math.min(p.amount, room);
      if (a > 0) { ps.push({ ...p, amount: a }); room -= a; }
    }
    if (room > 0) ps.push({ date: usDate(r['Invoice Date']), amount: room, method: 'Other', reference: 'Paid in Fullbay' });
    invoices.push({
      number: r['Number'], order: r['Order'], date: usDate(r['Invoice Date']) || usDate(r['Created Date']), customer: r['Customer'], unit: r['Unit'],
      po: r['PO'], writer: r['Service Writer'], terms: r['Credit Terms'] || '', mileage: many ? 0 : Math.round(num(ds[0]?.['Unit Miles'])),
      lines: use, supplies, tax: cents(r['Tax']), total, balance, payments: ps, note,
    });
  }
  if (fixed) notes.push(`${fixed} invoice${fixed > 1 ? 's were' : ' was'} changed in Fullbay after a credit or refund — brought in as summary lines that match Fullbay's totals`);
  const owed = invoices.reduce((a, i) => a + i.balance, 0);
  return {
    invoices, notes, owed, total: invoices.reduce((a, i) => a + i.total, 0), payments: invoices.reduce((a, i) => a + i.payments.length, 0),
    owing: new Set(invoices.filter(i => i.balance > 0).map(i => i.customer.toLowerCase())).size,
    missing: !list.length ? 'invoiceList' : !details.size ? 'invoiceDetails' : !pays.size ? 'paymentDetails' : '',
  };
}

/** What each part # was last sold for in Fullbay (Invoice details file): Fullbay's inventory export leaves most sell prices at $0,
 *  because Fullbay works them out from its markup matrix. Returns part # → price in cents (the latest invoice wins). */
export function lastPrices(sheets: FbSheet[]) {
  const out = new Map<string, { sku: string; price: number; date: string }>();
  for (const d of sheets.filter(s => historyKind(s.headers) === 'invoiceDetails').flatMap(objs)) {
    if (d['Type'] !== 'Part') continue;
    const sku = String(d['Part #'] || '').trim(), price = cents(d['Rate']), date = usDate(d['Invoice Date']);
    if (!sku || price <= 0) continue;
    const k = sku.toLowerCase(), cur = out.get(k);
    if (!cur || date >= cur.date) out.set(k, { sku, price, date });
  }
  return out;
}
